/* eslint-disable no-return-assign */
/* eslint-disable react/no-array-index-key */
import React, {
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Shimmer from 'react-shimmer-effect';
import moment from 'moment-timezone';
import UnifiedDayHeader from './UnifiedDayHeader';
import DraggableContainer from './DraggableContainer';
import Text from '../../../../shared/components/Text/Text';
import {
  setExpandedItems,
  setWeekViewExpandedDay,
  setCurrentlyDisplayedWeekdays,
  setSelectedNumberOfDaysToShowInWeek,
} from '../../ducks/calendarActions';

const WeekContainer = styled('div')`
  display: flex;
  width: 100%;
  height: 100%;
  gap: ${(props) => (props.viewType === 'weekView' ? '8px' : '1px')};
  background-color: #EEEEEE;
`;

const MarginRight = styled('div')`
  position: fixed;
  top: ${(props) => props.headerHeight + (props.programType === 'free-form-program' ? 0 : 32) + 49}px;
  bottom: 48px;
  right: 0;          
  width: 8px;       
  background-color: #F5F5F5;
`;

const MarginLeft = styled('div')`
  position: fixed;
  top: ${(props) => props.headerHeight + (props.programType === 'free-form-program' ? 0 : 32) + 49}px;
  bottom: 48px;
  left: ${(props) => `${(props.isPrimaryAnimatedSideBarOpen ? 384 : 0) + 48}px`};       
  width: 8px;       
  background-color: #F5F5F5;

  @media screen and (max-width: 1023px) {
    left: ${(props) => `${(props.isPrimaryAnimatedSideBarOpen ? 300 : 0) + 48}px`}; 
  }
  
`;

const DayWrapper = styled('div')`
  flex: ${(props) => (props.isExpanded ? '0 0 604px' : '1')};
  min-width: 175px;
  display: flex;
  flex-direction: column;
`;

const DayColumn = styled('div')`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 0 0 4px 4px;
  flex: 1;
  padding-top: ${(props) => !props.isWeekDayItemsLoading && '8px'};
`;

const StatsBar = styled('div')`
  background: white;
  min-height: 40px;
  border-top: 1px solid #EEEEEE;
  width: 100%;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const WeekView = ({ isWeekDayItemsLoading }) => {
  const dispatch = useDispatch();
  const weekViewContainerRef = useRef();
  const horizontalItemRefs = useRef([]);
  const shimmerCount = [1, 2, 3, 4, 5];
  const headerHeight = useSelector((state) => state.calendar.data.headerHeight);
  const selectedWeekViewDayToggle = useSelector(
    (state) => state.calendar.data.selectedWeekViewDayToggle,
  );
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const beforeRecurringWorkouts = useSelector(
    (state) => state.calendar.data.beforeRecurringWorkouts,
  );
  const afterRecurringWorkouts = useSelector((state) => state.calendar.data.afterRecurringWorkouts);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const isAnalyticsDisabled = useSelector(
    (state) => state.calendar.ui.isAnalyticsDisabled,
  );
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const calendarItems = useSelector((state) => state.calendar.data.calendarItems);
  const programItems = useSelector((state) => state.calendar.data.nonDateBasedProgramItems);
  const expandedDay = useSelector((state) => state.calendar.data.weekViewExpandedDay);
  const selectedNumberOfDaysToShowInWeek = useSelector(
    (state) => state.calendar.data.selectedNumberOfDaysToShowInWeek,
  );
  const selectedDaysOfWeekToShowInCurrentWeek = useSelector(
    (state) => state.calendar.data.selectedDaysOfWeekToShowInCurrentWeek,
  );
  const animatedSidebarContainerWidth = useSelector(
    (state) => state.calendar.data.animatedSidebarContainerWidth,
  );
  const isPrimaryAnimatedSideBarOpen = useSelector(
    (state) => state.calendar.data.isPrimaryAnimatedSideBarOpen,
  );
  const mostRecentlyAddedCalendarItem = useSelector(
    (state) => state.calendar.data.mostRecentlyAddedCalendarItem,
  );
  const weekViewHorizontallyExpandedDay = useSelector(
    (state) => state.calendar.data.weekViewExpandedDay,
  );

  const numberOfTimesSelectedDayOccursInMonth = () => {
    const formatDate = moment(selectedDate).format('YYYY-MM-DD');
    const start = moment(formatDate).startOf('month');
    const end = moment(formatDate).endOf('month');

    const countDays = (day) => (day.isAfter(end, 'day')
      ? 0
      : (day.format('dddd') === selectedWeekViewDayToggle ? 1 : 0) + countDays(day.clone().add(1, 'day')));

    return countDays(start);
  };

  useEffect(() => {
    if (selectedWeekViewDayToggle !== 'Everyday') {
      dispatch(setSelectedNumberOfDaysToShowInWeek(numberOfTimesSelectedDayOccursInMonth()));
    }
    if (selectedWeekViewDayToggle === 'Everyday') {
      dispatch(setSelectedNumberOfDaysToShowInWeek(7));
    }
  }, [selectedWeekViewDayToggle]);

  const hasRecurringExercisesAssociatedWithDate = (date) => {
    if (!date) return false;

    return (
      Object.keys(beforeRecurringWorkouts).includes(date)
    || Object.keys(afterRecurringWorkouts).includes(date)
    );
  };

  const weekDatesWhenDayToggleSelected = useMemo(() => {
    const daysToInclude = selectedDaysOfWeekToShowInCurrentWeek;
    const daysToShowMax = selectedNumberOfDaysToShowInWeek || 7;
    const includeEmptyDays = daysToInclude.includes('Empty Days');

    const selectedMoment = moment(selectedDate);
    const month = selectedMoment.month(); /** 0-indexed */
    const year = selectedMoment.year();

    const dates = [];
    const datesForReduxTracking = [];

    /** Start from the first day of the month */
    const current = moment().year(year).month(month).date(1);

    /** Loop through all days of the month */
    while (current.month() === month) {
      const dayName = current.format('dddd');
      const formattedDate = current.format('YYYY-MM-DD');

      const isDateEmpty = !calendarItems[formattedDate]?.exercises?.length
      && !hasRecurringExercisesAssociatedWithDate(formattedDate);

      if (
        /** see if dayName we a looping is === to the selected toggle */
        dayName === selectedWeekViewDayToggle
      && (!isDateEmpty || includeEmptyDays)
      ) {
        dates.push(current.clone());
        datesForReduxTracking.push({ date: formattedDate, isDateEmpty });

        if (dates.length >= daysToShowMax) {
          break;
        }
      }

      current.add(1, 'day');
    }

    if (selectedWeekViewDayToggle !== 'Everyday') {
      dispatch(setCurrentlyDisplayedWeekdays(datesForReduxTracking));
    }

    return dates;
  }, [
    selectedDate,
    selectedNumberOfDaysToShowInWeek,
    selectedDaysOfWeekToShowInCurrentWeek,
    calendarItems,
    selectedWeekViewDayToggle,
  ]);

  const weekDates = useMemo(() => {
    const daysToInclude = selectedDaysOfWeekToShowInCurrentWeek;
    const daysToShowMax = selectedNumberOfDaysToShowInWeek || 7;
    const includeEmptyDays = daysToInclude.includes('Empty Days');

    const dates = [];
    const datesForReduxTracking = [];

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < 7; i++) {
      const current = moment(selectedDate).clone().add(i, 'days');
      const dayName = current.format('dddd');
      const formattedDate = current.format('YYYY-MM-DD');

      const isDateEmpty = !calendarItems[formattedDate]?.exercises?.length
      && !hasRecurringExercisesAssociatedWithDate(formattedDate);

      /** Only add if day is toggled on AND (it's not empty OR we're allowing empty days) */
      if (
        daysToInclude.includes(dayName)
      && (!isDateEmpty || includeEmptyDays)
      ) {
        dates.push(current);
        datesForReduxTracking.push({ date: formattedDate, isDateEmpty });
      }

      if (dates.length >= daysToShowMax) {
        break;
      }
    }

    if (selectedWeekViewDayToggle === 'Everyday') {
      dispatch(setCurrentlyDisplayedWeekdays(datesForReduxTracking));
    }
    return dates;
  }, [
    selectedDate,
    selectedNumberOfDaysToShowInWeek,
    selectedDaysOfWeekToShowInCurrentWeek,
    calendarItems,
  ]);

  const programWeekDays = useMemo(() => {
    const selectedProgramDay = programItems[selectedProgramDayNumber];

    if (!selectedProgramDay || !programItems?.length > 0) return [0, 1, 2, 3, 4, 5, 6];

    const selectedWeek = selectedProgramDay.weekNumber;

    const dayNumbersInSelectedWeek = programItems
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.weekNumber === selectedWeek)
      .map(({ index }) => index);
    return dayNumbersInSelectedWeek;
  }, [programItems, selectedProgramDayNumber]);

  const handleToggleExpand = (dayKey) => {
    const isCurrentlyExpanded = expandedDay === dayKey;
    dispatch(setWeekViewExpandedDay(isCurrentlyExpanded ? null : dayKey));
    /** When toggling expanding a day horizontally
     * clear all expanded items to collapse all exercises */
    dispatch(setExpandedItems({}));
  };

  const filteredWeekDates = selectedWeekViewDayToggle !== 'Everyday' ? weekDatesWhenDayToggleSelected : weekDates;

  const expandDay = (dayKey) => {
    dispatch(setWeekViewExpandedDay(dayKey));
  };

  const weekDatesOrDays = programType === 'free-form-program' ? programWeekDays : filteredWeekDates;

  useEffect(() => {
    if (!weekViewHorizontallyExpandedDay || !Array.isArray(weekDatesOrDays)) return;

    /** Find the matching index */
    const matchingIndex = programType === 'free-form-program' ? weekDatesOrDays.findIndex((dayNumberIndex) => dayNumberIndex === weekViewHorizontallyExpandedDay) : weekDatesOrDays.findIndex((date) => moment(date).format('YYYY-MM-DD') === weekViewHorizontallyExpandedDay);

    if (matchingIndex === -1) {
      return;
    }

    const dayColumn = horizontalItemRefs.current[matchingIndex];
    if (dayColumn) {
      const delay = mostRecentlyAddedCalendarItem ? 400 : 100;
      setTimeout(() => {
        dayColumn.scrollIntoView({ behavior: 'smooth', inline: 'end', block: 'nearest' });
      }, delay);
    }
  }, [weekViewHorizontallyExpandedDay]);

  return (
    <WeekContainer
      ref={weekViewContainerRef}
      viewType={viewType}
    >
      {weekDatesOrDays?.map((dateOrDay, index) => {
        const dayKey = programType === 'free-form-program' ? dateOrDay : dateOrDay.format('YYYY-MM-DD');
        const isExpanded = expandedDay === dayKey;
        return (
          <>
            <DayWrapper
              key={`day-${dayKey}`}
              isExpanded={isExpanded}
              ref={(el) => (horizontalItemRefs.current[index] = el)}
            >
              <UnifiedDayHeader
                programType={programType}
                viewType='weekView'
                date={dateOrDay}
                isExpanded={isExpanded}
                onToggleExpand={() => handleToggleExpand(dayKey, index)}
                showWorkoutName
                isWeekView
                dateOrDay={dateOrDay}
                filteredWeekDates={filteredWeekDates}
              />
              <DayColumn isWeekDayItemsLoading={isWeekDayItemsLoading}>
                {isWeekDayItemsLoading ? (
                  shimmerCount.map((_, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex', paddingLeft: 8, paddingRight: 8, paddingTop: i === 0 ? 8 : 4, paddingBottom: 4,
                      }}
                    >
                      <Shimmer>
                        <div
                          style={{
                            height: 64,
                            backgroundColor: '#f5f5f5',
                            width: '100%',
                          }}
                        />
                      </Shimmer>
                    </div>
                  ))
                ) : (
                  <>
                    <DraggableContainer
                      id={programType === 'free-form-program' ? `week-day-${index}` : `week-day-${dayKey}`}
                      date={dateOrDay}
                      dayKey={dayKey}
                      isWeekView
                      isDayExpanded={isExpanded}
                      onDayExpand={() => handleToggleExpand(dayKey)}
                      expandDay={() => expandDay(dayKey)}
                    />
                    {((Object.keys(selectedReduxCalendar).length
                || Object.keys(selectedReduxAthlete).length
                || Object.keys(selectedReduxNonDateBasedProgram).length))
                && !isAnalyticsDisabled ? (
                  <StatsBar>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      gap: '16px',
                    }}
                    >
                      <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                      >
                        <Text fontSize='13px' color='#BDBDBD'>Sets</Text>
                        <Text
                          marginLeft='5px'
                          marginTop='2px'
                          fontSize='15px'
                          color='#616161'
                          fontWeight='500'
                        >
                          {(() => {
                            const endpointFormattedDate = moment(dateOrDay).format('YYYY-MM-DD');
                            return calendarItems[endpointFormattedDate]?.setCount || 0;
                          })()}
                        </Text>
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                      >
                        <Text fontSize='13px' color='#BDBDBD'>Reps</Text>
                        <Text
                          marginLeft='5px'
                          marginTop='2px'
                          fontSize='15px'
                          color='#616161'
                          fontWeight='500'
                        >
                          {(() => {
                            const endpointFormattedDate = moment(dateOrDay).format('YYYY-MM-DD');
                            return calendarItems[endpointFormattedDate]?.repCount || 0;
                          })()}
                        </Text>
                      </div>
                    </div>
                  </StatsBar>
                      ) : null}
                  </>
                )}
              </DayColumn>
            </DayWrapper>
          </>
        );
      })}
      {/* we need to add fixed position margins because
      nested scroll only work if we allow the browser to control scroll
      this mean we lose our margins that we would get from
      a nested scroll container so we are just creating them for style  */}
      <MarginLeft
        headerHeight={headerHeight}
        animatedSidebarContainerWidth={animatedSidebarContainerWidth}
        isPrimaryAnimatedSideBarOpen={isPrimaryAnimatedSideBarOpen}
        programType={programType}
      />
      <MarginRight headerHeight={headerHeight} programType={programType} />
    </WeekContainer>
  );
};

WeekView.propTypes = {
  isWeekDayItemsLoading: PropTypes.bool,
};

WeekView.defaultProps = {
  isWeekDayItemsLoading: false,

};

export default WeekView;
